import * as Sentry from "@sentry/react";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { PersistentDrawer } from "@/components/Drawer/PersistentDrawer";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { HotelDetailHeader } from "@/features/hotels/components/hotel/HotelDetailHeader";
import { HotelOverview } from "@/features/hotels/components/hotel/HotelOverview";
import {
  HotelSearchForm,
  type HotelSearchFormOnChangeSource,
} from "@/features/hotels/components/HotelSearchForm/HotelSearchForm";
import { PaymentOptions } from "@/features/hotels/components/PaymentOptions";
import { useHotelDataContext } from "@/features/hotels/contexts/HotelDataContext";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import type { HotelSearchFormInputValues } from "@/features/hotels/utils/hotelsValidation";
import { getTotalVisitors } from "@/features/hotels/utils/rooms";
import { HotelTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import { getDateString } from "@/utils/dates";

export const Route = createFileRoute(
  "/_authenticated/hotels/_hotels/_hotel-offer/$id/details",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

/**
 * First step of the hotel booking process.
 */
function RouteComponent() {
  const intl = useIntl();
  const navigate = Route.useNavigate();

  const { data: hotel } = useHotelDataContext();
  const { searchData, updateSearchData } = useHotelSearchDataContext();

  const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);

  if (!hotel) {
    // If the app fails to load the hotel data or to pass search data, redirect to the hotel list page
    // This is a fallback in case the user tries to access this page directly
    // It's unlikely that this will happen, but it's a good fallback to have
    // Avoids the need to handle these cases in the other components
    Sentry.captureMessage(
      "User tried to access hotel details page directly without going through the hotel offer page",
      "fatal",
    );
    return <Navigate to="/hotels/list" />;
  }

  const originalTotalPrice = hotel.minPrice?.value || 0;

  const handleSearchDrawerOpen = () => {
    setSearchDrawerOpen(true);

    if (!searchData) return;

    rudderAnalytics.track(
      HotelTrackingEvents.USER_CLICKED_EDIT_HOTEL_DETAILS_PAGE,
      {
        hotel_name: hotel.name,
        current_start_date: getDateString(searchData.dates.checkIn),
        current_end_date: getDateString(searchData.dates.checkOut),
        current_travellers_count: getTotalVisitors(searchData.rooms),
        current_rooms_count: searchData.rooms.length,
      },
    );
  };

  const handleSearchChange = (
    values: HotelSearchFormInputValues,
    source: HotelSearchFormOnChangeSource,
  ) => {
    if (searchData) {
      const oldCheckIn = getDateString(searchData.dates.checkIn);
      const oldCheckOut = getDateString(searchData.dates.checkOut);
      const newCheckIn = getDateString(values.dates.checkIn);
      const newCheckOut = getDateString(values.dates.checkOut);

      if (source === "dates") {
        const isStartDateChanged = oldCheckIn !== newCheckIn;
        const isEndDateChanged = oldCheckOut !== newCheckOut;

        rudderAnalytics.track(
          HotelTrackingEvents.USER_EDIT_DATES_HOTEL_DETAILS_PAGE,
          {
            ...(isStartDateChanged && {
              old_start_date: oldCheckIn,
              new_start_date: newCheckIn,
            }),
            ...(isEndDateChanged && {
              old_end_date: oldCheckOut,
              new_end_date: newCheckOut,
            }),
            is_changed: isStartDateChanged || isEndDateChanged,
          },
        );
      }

      const oldGuestCount = getTotalVisitors(searchData.rooms);
      const oldRoomCount = searchData.rooms.length;
      const newGuestCount = getTotalVisitors(values.rooms);
      const newRoomCount = values.rooms.length;

      if (source === "rooms") {
        const isGuestCountChanged = oldGuestCount !== newGuestCount;
        const isRoomCountChanged = oldRoomCount !== newRoomCount;

        rudderAnalytics.track(
          HotelTrackingEvents.USER_EDIT_ROOMSPAX_HOTEL_DETAILS_PAGE,
          {
            ...(isGuestCountChanged && {
              old_guest_count: oldGuestCount,
              new_guest_count: newGuestCount,
            }),
            ...(isRoomCountChanged && {
              old_room_count: oldRoomCount,
              new_room_count: newRoomCount,
            }),
            is_changed: isGuestCountChanged || isRoomCountChanged,
          },
        );
      }
    }

    updateSearchData((prev) =>
      prev ? { ...prev, dates: values.dates, rooms: values.rooms } : null,
    );
  };

  return (
    <>
      <div className="mb-2 px-main">
        <HotelDetailHeader
          hotel={hotel}
          onSearchDrawerOpen={handleSearchDrawerOpen}
        />
      </div>
      <div className="px-main">
        <HotelOverview hotel={hotel} />
      </div>
      <PersistentDrawer>
        <PaymentOptions totalPrice={originalTotalPrice} />

        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            navigate({ to: "/hotels/$id/rooms" });
          }}
        >
          {intl.formatMessage({
            defaultMessage: "Select a room",
            description:
              "Label for the button that takes the user to the next step of the hotel booking process, where they can select a room",
          })}
        </Button>
      </PersistentDrawer>

      <SwipeableDrawer
        open={searchDrawerOpen}
        onClose={() => setSearchDrawerOpen(false)}
      >
        <HotelSearchForm
          analyticsSource="sticky"
          variant="compact"
          initialValues={searchData}
          onChange={handleSearchChange}
        />
      </SwipeableDrawer>
    </>
  );
}
